import { routeHref } from '../../lib/router.js'
import { bareAddress, decorate, person, peopleTitle, sourceLabel, threadPeople } from '../../lib/mail.js'
import { shortDate } from '../../lib/time.js'

const Fez = window.Fez;
if (!Fez) throw new Error('fez runtime not loaded: load fez (window.Fez) before component modules');

// --- mail-list ---
let __fez_component_0;
{
const TITLES = { inbox: 'Inbox', feed: 'Feed', papertrail: 'Paper Trail', starred: 'Starred', assigned: 'Assigned to me', notes: 'Notes', snoozed: 'Snoozed', drafts: 'Drafts', sent: 'Sent', archive: 'Archive', spam: 'Spam', trash: 'Trash', all: 'All mail' }
const FOLDER_LABELS = { inbox: 'Inbox', archive: 'Archive', spam: 'Spam', trash: 'Trash', sent: 'Sent' }
const EMPTY = {
  inbox: ['tray', 'Inbox zero', 'Nothing new here.'],
  feed: ['newspaper', 'Nothing to read', 'Move a sender to the Feed and their newsletters gather here, never in the Inbox.'],
  papertrail: ['receipt', 'No paper trail', 'Move a sender to the Paper Trail and their receipts and notices are kept here.'],
  starred: ['star', 'No starred mail', 'Star a message to keep it here.'],
  assigned: ['user-circle-check', 'Nothing assigned to you', 'Conversations someone gives you show up here.'],
  notes: ['note', 'No notes', 'Add a note under a conversation and it shows up here.'],
  mynotes: ['note', 'No notes by you', 'Conversations you add a note to show up here; All lists your team\'s.'],
  snoozed: ['clock', 'Nothing snoozed', 'Snooze a conversation to have it come back later.'],
  label: ['tag', 'Nothing with this label', 'Label a conversation from its reader.'],
  drafts: ['file-text', 'No drafts', 'Save one from Compose.'],
  sent: ['paper-plane-tilt', 'Nothing sent yet', 'Mail you send shows up here.'],
  archive: ['archive', 'Archive is empty', 'Archived mail waits here.'],
  spam: ['warning-octagon', 'No spam', 'Mail flagged as spam lands here.'],
  trash: ['trash', 'Trash is empty', 'Deleted mail stays here for a while.'],
  all: ['mailbox', 'No mail yet', 'Every message shows here.'],
}
// The inbox's views by category; a row outside its own view says where it lives.
const CATEGORY_VIEWS = ['feed', 'papertrail']
const CATEGORY_PILLS = {
  feed: { label: 'Feed', icon: 'newspaper' },
  papertrail: { label: 'Paper Trail', icon: 'receipt' },
  new: { label: 'New sender', icon: 'user-plus' },
}
// Wide enough to reload everything loaded in one call, small enough for the API.
const PAGE = 50
const MAX = 200

const when = (item) => Date.parse(item.last_at || item.received_at || item.updated_at || '') || 0
__fez_component_0 = class {
  PROPS = {
    container: String,
    mailbox: String,
    host: String,
    unread: Boolean,
    boxes: Array,
    folder: { type: String, default: 'inbox' },
    addr: String,
    label: String,
    labels: Array,
    source: String,
    sources: Array,
    q: String,
    mode: { type: String, default: 'threads' },
    open: String,
    tick: Number,
    addresses: Array,
    own: Array,
    can_delete: Boolean,
    on_menu: { type: Function, state: true, default: () => {} },
    on_mode: { type: Function, state: true, default: () => {} },
  }

  init() {
    this.state.loading = true
    this.state.loadingMore = false
    this.state.error = ''
    this.state.searchError = ''
    this.state.items = []
    this.state.cursor = ''
    this.state.seq = 0
    this.state.selected = {}
    this.state.notes = Fez.localStorage.get('mail-notes', 'mine')
  }

  onMount() {
    this.state.observer = new IntersectionObserver((entries) => entries.some((entry) => entry.isIntersecting) && this.loadMore(), { root: this.state.scroller, rootMargin: '400px' })
    this.state.observer.observe(this.state.sentinel)
  }

  onDestroy() {
    this.state.observer?.disconnect()
  }

  onRefresh(props, changed) {
    if ('q' in changed && this.state.searchInput) this.state.searchInput.value = props.q || ''
    if (['container', 'mailbox', 'host', 'folder', 'addr', 'label', 'source', 'q', 'mode', 'unread'].some((key) => key in changed)) {
      this.state.searchError = ''
      this.state.selected = {}
      this.load(false)
    } else if ('tick' in changed) {
      this.load(true)
    }
  }

  // Assigned, notes, snoozed and labeled always list conversations: those belong to a conversation.
  threads() {
    return this.props.mode === 'threads' || ['assigned', 'notes', 'snoozed'].includes(this.folder()) || Boolean(this.props.label)
  }

  folder() {
    return this.props.folder
  }

  // Notes: conversations I noted (`mine`) or anyone's (`all`), remembered in the browser.
  setNotes(view) {
    Fez.localStorage.set('mail-notes', view)
    this.state.notes = view
    this.state.selected = {}
    this.load(false)
  }

  // The request for one page: path and params. Every mailbox in view is listed in one
  // merged page (`mailbox/threads`, `mailbox/emails`).
  request(cursor, limit) {
    const { mailbox, addr, q } = this.props
    const folder = this.folder()
    const [address, local] = (addr || '').split('@')
    const host = local || this.props.host
    const category = CATEGORY_VIEWS.includes(folder) ? folder : undefined
    const base = { mailbox: mailbox || undefined, address: address || undefined, host: host || undefined, source: this.props.source || undefined, folder: category ? 'inbox' : ['all', 'starred', 'assigned', 'notes', 'snoozed'].includes(folder) || this.props.label ? undefined : folder, category, limit, cursor: cursor || undefined }
    const query = [{ starred: 'is:starred', notes: this.state.notes === 'all' ? 'has:note' : 'has:mynote' }[folder] || '', this.props.unread ? 'is:unread' : '', q || ''].filter(Boolean).join(' ')
    const threads = { ...base, q: query || undefined, assignee: folder === 'assigned' ? 'me' : undefined, snoozed: folder === 'snoozed' || undefined, label: this.props.label || undefined }
    return this.threads() ? ['mailbox/threads', threads] : ['mailbox/emails', { ...base, q: query || undefined }]
  }

  async page(cursor, limit) {
    const [path, params] = this.request(cursor, limit)
    const page = await window.App.api(path, params, { silent: true, envelope: true })
    return { items: page.data.items.map((item) => this.own(item)), cursor: page.meta?.next_cursor || '' }
  }

  // Every row knows its container and mailbox.
  own(item) {
    const container = item.container || this.props.container
    return { ...item, container, src: `${container}/${item.mailbox}` }
  }

  // `keep` reloads what is already loaded (a live change) without a spinner or losing the scroll.
  async load(keep) {
    const seq = ++this.state.seq
    const folder = this.folder()
    if (!keep) this.state.scroller.scrollTop = 0
    try {
      if (folder === 'drafts') {
        const lists = await Promise.all(this.props.boxes.map((scope) => window.App.api(`container/${scope.container}/drafts`, { mailbox: scope.mailbox || undefined }, { silent: true }).then((list) => list.items.map((draft) => ({ ...this.own({ ...draft, container: scope.container }), isDraft: true })))))
        if (seq !== this.state.seq) return
        this.state.items = lists.flat().sort((a, b) => when(b) - when(a))
        this.state.cursor = ''
      } else {
        const limit = keep ? Math.min(MAX, Math.max(PAGE, this.state.items.length)) : PAGE
        const page = await this.page('', limit)
        if (seq !== this.state.seq) return
        this.state.items = page.items
        this.state.cursor = page.cursor
      }
      this.state.error = ''
    } catch (error) {
      if (seq !== this.state.seq) return
      if (error.code === 'validation_failed' && error.details?.q) this.state.searchError = error.details.q
      else this.state.error = error.message
      this.state.items = []
      this.state.cursor = ''
    } finally {
      if (seq === this.state.seq) this.state.loading = false
    }
  }

  async loadMore() {
    if (!this.state.cursor || this.state.loadingMore || this.state.loading) return
    const seq = this.state.seq
    this.state.loadingMore = true
    try {
      const page = await this.page(this.state.cursor, PAGE)
      if (seq !== this.state.seq) return
      this.state.items = this.state.items.concat(page.items)
      this.state.cursor = page.cursor
    } catch (error) {
      window.Toast?.error(error.message)
    } finally {
      this.state.loadingMore = false
    }
  }

  // Without a mailbox filter a row's link names its mailbox (`box`) for the reader.
  href(params, row) {
    const { folder, addr, q } = this.props
    const { container, mailbox, host, label, source } = this.props
    return routeHref('inbox', { container, mailbox, folder: folder === 'inbox' ? '' : folder, unread: this.props.unread ? '1' : '', host, addr, label, source, q, box: mailbox ? '' : row?.mailbox, ...params })
  }

  search(event) {
    event.preventDefault()
    const q = (this.state.searchInput.value || '').trim()
    window.App.go(this.href({ q, thread: '', email: '' }))
  }

  clearSearch() {
    this.state.searchInput.value = ''
    window.App.go(this.href({ q: '' }))
  }

  focusSearch() {
    this.state.searchInput.focus()
    this.state.searchInput.select()
  }

  // A draft opens in a compose window when the dock is there, else on the compose page.
  openRow(key) {
    const row = this.state.rows.find((item) => item.key === key)
    if (!row || Date.now() - (this.state.swiped || 0) < 400) return
    if (row.kind === 'draft' && window.ComposeDock) window.ComposeDock.open({ container: row.container, mailbox: row.mailbox, draft: row.id })
    else window.App.go(row.href)
  }

  // Opens the row `delta` away from the open one (j/k); 0 opens the first when nothing is open.
  step(delta) {
    const rows = this.state.rows.filter((row) => row.kind !== 'draft')
    if (!rows.length) return
    const index = rows.findIndex((row) => row.active)
    if (index >= 0 && delta === 0) return
    const next = index < 0 ? 0 : Math.min(rows.length - 1, Math.max(0, index + delta))
    if (next === index) return
    window.App.go(rows[next].href)
    this.find(`[data-key="${rows[next].key}"]`)?.scrollIntoView({ block: 'nearest' })
    if (next >= rows.length - 3) this.loadMore()
  }

  // Shift extends the selection from the last row picked.
  toggleSelect(key, event) {
    event?.stopPropagation()
    const rows = this.state.rows.filter((row) => row.kind !== 'draft')
    const index = rows.findIndex((row) => row.key === key)
    const on = !this.state.selected[key]
    const selected = { ...this.state.selected }
    const from = event?.shiftKey && this.state.anchor !== undefined ? Math.min(this.state.anchor, index) : index
    const to = event?.shiftKey && this.state.anchor !== undefined ? Math.max(this.state.anchor, index) : index
    for (const row of rows.slice(from, to + 1)) {
      if (on) selected[row.key] = true
      else delete selected[row.key]
    }
    this.state.anchor = index
    this.state.selected = selected
  }

  toggleActive() {
    const row = this.state.rows.find((item) => item.active)
    if (row) this.toggleSelect(row.key)
  }

  selectAll() {
    this.state.selected = Object.fromEntries(this.state.rows.filter((row) => row.kind !== 'draft').map((row) => [row.key, true]))
  }

  clearSelection() {
    this.state.selected = {}
  }

  hasSelection() {
    return Object.keys(this.state.selected).length > 0
  }

  // One `mail_update` per mailbox: conversations by thread, messages by id.
  async update(rows, change, label) {
    const groups = {}
    for (const row of rows) {
      const group = (groups[row.src] ||= { container: row.container, mailbox: row.mailbox, emails: [], threads: [] })
      if (row.kind === 'thread') group.threads.push(row.key)
      else group.emails.push(row.id)
    }
    try {
      await Promise.all(
        Object.values(groups).map((group) =>
          window.App.api(`container/${group.container}/mail_update`, { mailbox: group.mailbox, emails: group.emails, threads: group.threads, ...change }, { silent: true }),
        ),
      )
      if (label) window.Toast.success(label)
    } catch (error) {
      window.Toast.error(error.message)
    }
    this.state.selected = {}
    await this.load(true)
  }

  bulk(folder) {
    const rows = this.state.rows.filter((row) => this.state.selected[row.key])
    if (!rows.length) return
    const count = rows.length === 1 ? 'One conversation' : `${rows.length} conversations`
    const label = { archive: `${count} archived`, trash: `${count} moved to the trash`, inbox: `${count} moved to the inbox`, spam: `${count} marked as spam` }[folder]
    return this.update(rows, { folder }, label)
  }

  mark(what) {
    const rows = this.state.rows.filter((row) => this.state.selected[row.key])
    const change = { read: { seen: true }, unread: { seen: false }, star: { flagged: true } }[what]
    if (rows.length && change) return this.update(rows, change)
  }

  async emptyFolder() {
    const folder = this.folder()
    // Spam in the mail app: every box in view I may send from.
    if (folder === 'spam') {
      const ok = await window.Dialog.confirm({ title: 'Delete all spam?', text: 'Every message in Spam is deleted for good.', confirm: 'Delete all', danger: true })
      if (!ok) return
      try {
        const results = await Promise.all(this.props.boxes.filter((box) => box.can_send).map((box) => window.App.api(`container/${box.container}/folder_empty`, { folder: 'spam', mailbox: box.mailbox }, { silent: true })))
        window.Toast.success(`${results.reduce((sum, result) => sum + result.deleted, 0)} deleted for good`)
      } catch (error) {
        window.Toast.error(error.message)
      }
      await this.load(false)
      return
    }
    const ok = await window.Dialog.confirm({ title: `Empty ${folder}?`, text: `Every message in ${folder} is deleted for good.`, confirm: `Empty ${folder}`, danger: true })
    if (!ok) return
    await window.App.api(`container/${this.props.container}/folder_empty`, { folder, mailbox: this.props.mailbox || undefined })
    await this.load(false)
  }

  // Touch only: a row dragged sideways follows the finger; past 96px it is archived (right) or trashed (left).
  swipeStart(event, key) {
    if (event.pointerType !== 'touch') return
    const row = this.state.rows.find((item) => item.key === key)
    if (!row || row.kind === 'draft') return
    const el = event.currentTarget
    const start = { x: event.clientX, y: event.clientY }
    let dx = 0
    let active = false
    const move = (e) => {
      dx = e.clientX - start.x
      if (!active && Math.abs(dx) > 12 && Math.abs(dx) > Math.abs(e.clientY - start.y)) {
        active = true
        el.setPointerCapture(e.pointerId)
      }
      if (!active) return
      el.style.transform = `translateX(${dx}px)`
      el.dataset.swipe = dx > 0 ? 'archive' : 'trash'
    }
    const end = () => {
      el.removeEventListener('pointermove', move)
      el.removeEventListener('pointerup', end)
      el.removeEventListener('pointercancel', end)
      el.style.transform = ''
      delete el.dataset.swipe
      if (!active) return
      this.state.swiped = Date.now()
      if (Math.abs(dx) > 96) this.update([row], { folder: dx > 0 ? 'archive' : 'trash' }, dx > 0 ? 'Archived' : 'Moved to the trash')
    }
    el.addEventListener('pointermove', move)
    el.addEventListener('pointerup', end)
    el.addEventListener('pointercancel', end)
  }

  async toggleStar(id, event) {
    event.stopPropagation()
    const item = this.state.items.find((email) => email.id === id)
    if (!item) return
    const state = await window.App.api(`container/${item.container}/email_update`, { email: id, mailbox: item.mailbox, flagged: !item.flagged })
    this.state.items = this.state.items.map((email) => (email.id === id ? { ...email, ...state } : email))
  }

  async deleteDraft(ref, event) {
    event.stopPropagation()
    const draft = this.state.items.find((item) => item.ref === ref)
    if (!draft) return
    await window.App.api(`container/${draft.container}/draft_delete`, { draft: ref })
    this.state.items = this.state.items.filter((item) => item.ref !== ref)
  }

  // The `to alias@host` pill: only when the mailbox has several addresses and this one is not the first.
  via(address) {
    if (this.props.addresses.length < 2 || this.props.addr || !address) return ''
    const bare = bareAddress(address)
    return bare === bareAddress(this.props.addresses[0]) ? '' : address.toLowerCase()
  }

  // The source pill of forwarded mail; left out while the list shows one source only.
  from(ref) {
    if (!ref || this.props.source) return null
    const source = this.props.sources.find((item) => item.ref === ref)
    return source ? { label: sourceLabel(source), color: source.color, title: `Forwarded from ${source.address || `an account at ${source.forwarder}`}` } : null
  }

  // The mailbox pill: when the list spans several mailboxes.
  boxName(mailbox) {
    if (this.props.mailbox || this.props.boxes.length < 2) return ''
    return this.props.boxes.find((box) => box.mailbox === mailbox)?.name || ''
  }

  threadRow(thread, own) {
    const people = threadPeople(thread.participants, own)
    const more = thread.more_participants || 0
    return {
      key: thread.thread,
      kind: 'thread',
      src: thread.src,
      container: thread.container,
      mailbox: thread.mailbox,
      selected: Boolean(this.state.selected[thread.thread]),
      href: this.href({ thread: thread.thread, email: '' }, thread),
      active: this.props.open === thread.thread,
      people,
      more,
      title: peopleTitle(thread.participants.map((item) => person(item.name, item.address))) + (more > 0 ? `, and ${more} more` : ''),
      avatar: people[0].name,
      subject: thread.subject || '(no subject)',
      preview: thread.preview || '',
      when: shortDate(thread.last_at),
      unread: thread.unread_count > 0,
      count: thread.email_count > 1 ? thread.email_count : 0,
      via: thread.direction === 'in' ? this.via(thread.address) : '',
      from: thread.direction === 'in' ? this.from(thread.source) : null,
      box: this.boxName(thread.mailbox),
      assignee: !thread.assignee ? '' : thread.assignee === this.globalState.me?.email ? 'You' : thread.assignee.split('@')[0],
      assigneeTitle: thread.assignee ? `Assigned to ${thread.assignee}` : '',
      done: thread.status === 'done',
      snoozed: thread.snoozed_until ? `Until ${shortDate(thread.snoozed_until)}` : '',
      labelPills: (thread.labels || []).map((ref) => this.props.labels.find((label) => label.ref === ref)).filter(Boolean),
      category: this.categoryPill(thread.category),
    }
  }

  emailRow(email, spans) {
    decorate(email)
    const folder = spans && email.folder !== this.props.folder ? FOLDER_LABELS[email.folder] || '' : ''
    const out = email.direction === 'out'
    const to = (email.to || []).map((item) => person(item.name, item.address))
    const people = out ? (to.length ? to : [person('', email.address)]) : [person(email.from_name || (email.from_addr ? '' : 'Unknown sender'), email.from_addr)]
    return {
      key: email.id,
      kind: 'email',
      id: email.id,
      src: email.src,
      container: email.container,
      mailbox: email.mailbox,
      selected: Boolean(this.state.selected[email.id]),
      href: this.href({ email: email.id, thread: '' }, email),
      active: this.props.open === email.id,
      to: out,
      people,
      title: `${out ? 'To: ' : ''}${peopleTitle(people)}`,
      avatar: out ? email.toLabel : email.from_name || email.from_addr || '?',
      subject: email.subject || '(no subject)',
      preview: email.preview || '',
      when: shortDate(email.received_at),
      unread: email.unread,
      flagged: email.flagged,
      files: email.file_count,
      folder,
      sendLabel: email.sendLabel,
      sendTone: email.sendTone,
      via: email.direction === 'in' ? this.via(email.address) : '',
      from: email.direction === 'in' ? this.from(email.source) : null,
      box: this.boxName(email.mailbox),
      category: this.categoryPill(email.category),
    }
  }

  // A Feed, Paper Trail or new sender conversation says so outside its own view.
  categoryPill(category) {
    if (!category || category === 'inbox' || category === this.folder()) return null
    return CATEGORY_PILLS[category] || null
  }

  draftRow(draft) {
    return {
      key: draft.ref,
      kind: 'draft',
      id: draft.ref,
      container: draft.container,
      mailbox: draft.mailbox,
      box: this.boxName(draft.mailbox),
      href: routeHref('compose', { container: draft.container, mailbox: draft.mailbox, draft: draft.ref }),
      who: draft.to ? `To: ${draft.to}` : 'No recipient yet',
      subject: draft.subject || '(no subject)',
      preview: (draft.body || '').replace(/\s+/g, ' ').trim().slice(0, 160),
      when: shortDate(draft.updated_at),
    }
  }

  beforeRender() {
    const folder = this.folder()
    const own = new Set((this.props.own || []).map(bareAddress))
    // Folders that span several real folders tag each row with its own.
    const spans = ['all', 'starred', 'assigned', 'notes', 'snoozed'].includes(folder) || Boolean(this.props.q) || Boolean(this.props.label)
    this.state.rows = this.state.items.map((item) => (item.isDraft ? this.draftRow(item) : this.threads() && item.thread && item.email_count ? this.threadRow(item, own) : this.emailRow(item, spans)))
    for (const row of this.state.rows) row.tags = Boolean(row.box || row.via || row.from || row.category || row.folder || row.files || row.sendLabel || row.assignee || row.done || row.snoozed || row.labelPills?.length)
    const label = this.props.label && this.props.labels.find((item) => item.ref === this.props.label)
    this.state.title = label ? label.name : TITLES[folder] || 'Inbox'
    this.state.modes = !['drafts', 'assigned', 'notes', 'snoozed'].includes(folder) && !this.props.label
    this.state.notesSwitch = folder === 'notes' && !this.props.label
    // Next up tops the Inbox: a person's until they hide it, a mailbox session's always.
    const me = this.globalState.me
    this.state.nextUp = folder === 'inbox' && !this.props.q && !this.props.label && (me ? me.next_up : Boolean(this.globalState.mailboxSession))
    const empty = this.props.label && !this.props.q ? EMPTY.label : this.props.q ? ['magnifying-glass', 'No matches', `Nothing matches "${this.props.q}".`] : this.props.unread ? ['envelope-open', 'All read', 'No unread mail here.'] : EMPTY[folder === 'notes' && this.state.notes !== 'all' ? 'mynotes' : folder] || EMPTY.inbox
    const source = this.props.source && this.props.sources.find((item) => item.ref === this.props.source)
    const host = this.props.host
    const addr = this.props.addr && (this.props.addr.includes('@') ? this.props.addr : `${this.props.addr}@${host || ''}`)
    const only = source ? ` Only mail forwarded from ${source.address || source.forwarder} is shown.` : addr ? ` Only mail to ${addr} is shown.` : host ? ` Only mail to ${host} is shown.` : ''
    this.state.empty = { icon: empty[0], title: empty[1], text: `${empty[2]}${only}` }
    this.state.showEmpty = !this.state.loading && !this.state.error && !this.state.rows.length
    // Feed and Paper Trail keep saying what they are below their rows.
    this.state.help = CATEGORY_VIEWS.includes(folder) && !this.props.q && !this.props.label ? { icon: EMPTY[folder][0], text: EMPTY[folder][2] } : null
    const picked = this.state.rows.filter((row) => row.selected).length
    this.state.picked = picked > 0
    this.state.pickedLabel = `${picked} selected`
    this.state.allPicked = picked > 0 && picked === this.state.rows.filter((row) => row.kind !== 'draft').length
    this.state.canStar = picked > 0 && !this.threads()
    this.state.canInbox = folder !== 'inbox' && !CATEGORY_VIEWS.includes(folder)
    const appSpam = folder === 'spam'
    this.state.canEmpty = appSpam ? this.state.rows.length > 0 && this.props.boxes.some((box) => box.can_send) : this.props.can_delete && Boolean(this.props.mailbox) && folder === 'trash' && this.state.rows.length > 0
    this.state.emptyLabel = folder === 'spam' ? 'Delete all' : 'Empty'
  }

  CSS = `:fez {
display:flex;flex-direction:column;flex:1;min-height:0;.list-head{display:flex;align-items:center;gap:var(--space-2);min-height:var(--header-height);padding:var(--space-2) var(--space-3) var(--space-2) var(--space-4)}.list-menu{display:none}.list-title{flex:1;min-width:0;overflow:hidden;font-size:var(--text-lg);font-weight:var(--weight-bold);letter-spacing:-0.01em;text-overflow:ellipsis;white-space:nowrap}.list-modes{display:flex;gap:2px;padding:2px;border-radius:var(--radius-md);background:var(--color-bg-subtle)}.list-mode{display:grid;place-items:center;width:30px;height:26px;padding:0;border:0;border-radius:var(--radius-sm);background:transparent;color:var(--color-text-subtle);cursor:pointer;&:hover{color:var(--color-text)}&.on{background:var(--color-bg-raised);color:var(--color-text);box-shadow:var(--shadow-sm)}}.list-mode-text{width:auto;padding:0 var(--space-3);font:inherit;font-size:var(--text-sm);font-weight:var(--weight-semibold)}.list-search{display:flex;align-items:center;gap:var(--space-2);height:38px;margin:0 var(--space-3) var(--space-2);padding:0 var(--space-2) 0 var(--space-3);border:1px solid transparent;border-radius:var(--radius-full);background:var(--color-bg-subtle);color:var(--color-text-subtle);transition:border-color var(--duration-fast) var(--ease),background var(--duration-fast) var(--ease);&:focus-within{border-color:var(--color-focus);background:var(--color-bg-raised)}}.list-search-input{flex:1;min-width:0;height:100%;padding:0;border:0;outline:0;background:transparent;color:var(--color-text);font:inherit;font-size:var(--text-sm);&::-webkit-search-cancel-button{display:none}}.list-search-clear{display:grid;place-items:center;width:26px;height:26px;padding:0;border:0;border-radius:var(--radius-full);background:transparent;color:var(--color-text-subtle);cursor:pointer;&:hover{background:var(--color-bg-hover);color:var(--color-text)}}.list-search-error{margin:0 var(--space-4) var(--space-2);color:var(--color-danger);font-size:var(--text-xs);font-weight:var(--weight-medium);a{margin-left:var(--space-2);color:var(--color-text-muted);text-decoration:underline}}.list-scroll{flex:1;min-height:0;overflow-y:auto;border-top:1px solid var(--color-border)}.mrow{position:relative;display:flex;touch-action:pan-y;align-items:flex-start;gap:var(--space-3);padding:var(--space-3) var(--space-3) var(--space-3) var(--space-4);border-bottom:1px solid var(--color-border);cursor:pointer;transition:background var(--duration-fast) var(--ease);&:hover{background:var(--color-bg-hover);.mrow-star{opacity:1}}&.active{background:var(--color-selected-bg)}&.active::before{content:'';position:absolute;top:0;bottom:0;left:0;width:3px;background:var(--color-selected-text)}&.unread{.mrow-who,.mrow-subject{color:var(--color-text);font-weight:var(--weight-bold)}.mrow-dot{background:var(--color-accent)}}}.mrow-dot{position:absolute;top:calc(var(--space-3) + 15px);left:6px;width:6px;height:6px;border-radius:var(--radius-full)}.list-bulk{display:flex;flex:1;align-items:center;gap:var(--space-1);min-width:0}.list-bulk-count{margin:0 var(--space-2);font-size:var(--text-sm);font-weight:var(--weight-semibold);white-space:nowrap}.list-link{padding:0;border:0;background:none;color:var(--color-link);font:inherit;font-size:var(--text-sm);cursor:pointer;white-space:nowrap;&:hover{text-decoration:underline}}.list-grow{flex:1}.mrow-pick{position:relative;display:grid;place-items:center;flex-shrink:0;width:36px;height:36px;padding:0;border:0;border-radius:var(--radius-full);background:transparent;color:var(--color-text-muted);cursor:pointer}.mrow-check{display:none}.mrow.selected,.mrow.selected:hover{background:var(--color-highlight-bg);.mrow-check{color:var(--color-accent)}}.mrow[data-swipe]{transition:none}.mrow[data-swipe="archive"]{background:var(--color-success-soft)}.mrow[data-swipe="trash"]{background:var(--color-danger-soft)}@media (hover:hover){.mrow:hover .mrow-face{display:none}.mrow:hover .mrow-check{display:grid}}.list-selecting{.mrow-face{display:none}.mrow-check{display:grid}}.mrow-draft{display:grid;place-items:center;flex-shrink:0;width:36px;height:36px;border-radius:var(--radius-full);background:var(--color-bg-subtle);color:var(--color-text-muted)}.mrow-main{display:grid;gap:2px;flex:1;min-width:0}.mrow-line{display:flex;align-items:baseline;gap:var(--space-2);min-width:0}.mrow-who{flex:1;min-width:0;overflow:hidden;color:var(--color-text);font-weight:var(--weight-medium);text-overflow:ellipsis;white-space:nowrap}.mrow-email,.mrow-more{font-size:var(--text-xs);font-weight:var(--weight-normal)}.mrow-email{color:var(--color-text-muted)}.mrow-more{color:var(--color-text-subtle)}.mrow-count{color:var(--color-text-subtle);font-size:var(--text-xs);font-weight:var(--weight-semibold)}.mrow-when{flex-shrink:0;color:var(--color-text-subtle);font-size:var(--text-xs);font-variant-numeric:tabular-nums}.mrow-subject,.mrow-preview{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.mrow-subject{color:var(--color-text);font-size:var(--text-sm)}.mrow-preview{color:var(--color-text-muted);font-size:var(--text-sm)}.mrow-tags{display:flex;flex-wrap:wrap;align-items:center;gap:var(--space-1);margin-top:var(--space-1)}.mrow-pill{display:inline-flex;align-items:center;gap:3px;max-width:100%;padding:1px var(--space-2);overflow:hidden;border-radius:var(--radius-full);background:var(--color-bg-subtle);color:var(--color-text-muted);font-size:var(--text-2xs);font-weight:var(--weight-semibold);text-overflow:ellipsis;white-space:nowrap;&.via{background:var(--color-accent-soft);color:var(--color-accent)}&.box{background:var(--color-bg-hover);color:var(--color-text)}&.tag{border:1px solid var(--tag);background:transparent;color:var(--tag)}&.owner{background:var(--color-warning-soft);color:var(--color-warning)}&.done{background:var(--color-success-soft);color:var(--color-success)}}.mrow-star{display:grid;place-items:center;flex-shrink:0;width:28px;height:28px;margin:-2px -4px 0 0;padding:0;border:0;border-radius:var(--radius-md);background:transparent;color:var(--color-text-subtle);cursor:pointer;opacity:0;transition:opacity var(--duration-fast) var(--ease);&:hover,&:focus-visible{background:var(--color-bg-subtle);color:var(--color-text);opacity:1}&.on{color:var(--color-warning);opacity:1}}.list-state{padding:var(--space-10) var(--space-4)}.list-more{height:1px}.list-loading{display:flex;justify-content:center;padding:var(--space-4)}@media (max-width:1099px){.list-menu{display:grid}}@media (hover:none){.mrow-star{opacity:1}}
}`

  HTML = `<header class="list-head">
{#if state.picked}
<div class="list-bulk" fez:in="fade, duration=120">
<button type="button" class="list-mode" title="Clear the selection (Esc)" onclick="fez.clearSelection()"><ui-icon name="x" size="16"></ui-icon></button>
<span class="list-bulk-count">{state.pickedLabel}</span>
{#unless state.allPicked}<button type="button" class="list-link" onclick="fez.selectAll()">Select all</button>{/unless}
<span class="list-grow"></span>
<ui-button size="sm" variant="ghost" icon="archive" title="Archive (e)" onclick="fez.bulk('archive')"></ui-button>
<ui-button size="sm" variant="ghost" icon="trash" title="Move to trash (#)" onclick="fez.bulk('trash')"></ui-button>
{#if state.canInbox}<ui-button size="sm" variant="ghost" icon="tray" title="Move to inbox" onclick="fez.bulk('inbox')"></ui-button>{/if}
<ui-button size="sm" variant="ghost" icon="envelope-open" title="Mark read" onclick="fez.mark('read')"></ui-button>
<ui-button size="sm" variant="ghost" icon="envelope-simple" title="Mark unread" onclick="fez.mark('unread')"></ui-button>
{#if state.canStar}<ui-button size="sm" variant="ghost" icon="star" title="Star" onclick="fez.mark('star')"></ui-button>{/if}
</div>
{:else}
<span class="list-menu"><ui-button variant="ghost" icon="list" title="Mailboxes and folders" onclick="fez.state.on_menu()"></ui-button></span>
<h1 class="list-title">{state.title}</h1>
{#if state.canEmpty}
<ui-button size="sm" variant="danger-ghost" icon="trash" label={state.emptyLabel} title="Delete everything here for good" onclick="fez.emptyFolder()"></ui-button>
{/if}
{#if state.modes}
<div class="list-modes" role="group" aria-label="List mode">
<button type="button" class="list-mode" class:on={props.mode === 'threads'} title="Conversations" aria-pressed={props.mode === 'threads' ? 'true' : 'false'} onclick="fez.state.on_mode('threads')"><ui-icon name="chats" size="18"></ui-icon></button>
<button type="button" class="list-mode" class:on={props.mode === 'messages'} title="Messages" aria-pressed={props.mode === 'messages' ? 'true' : 'false'} onclick="fez.state.on_mode('messages')"><ui-icon name="list-bullets" size="18"></ui-icon></button>
</div>
{/if}
{#if state.notesSwitch}
<div class="list-modes" role="group" aria-label="Whose notes">
<button type="button" class="list-mode list-mode-text" class:on={state.notes !== 'all'} title="Conversations you added a note to" aria-pressed={state.notes !== 'all' ? 'true' : 'false'} onclick="fez.setNotes('mine')">My</button>
<button type="button" class="list-mode list-mode-text" class:on={state.notes === 'all'} title="Conversations with a note from anyone" aria-pressed={state.notes === 'all' ? 'true' : 'false'} onclick="fez.setNotes('all')">All</button>
</div>
{/if}
{/if}
</header>
<form class="list-search" role="search" onsubmit="fez.search(event)">
<ui-icon name="magnifying-glass" size="16"></ui-icon>
<input class="list-search-input" type="search" name="q" placeholder="Search mail" aria-label="Search mail" title="Words, &quot;phrases&quot;, from: to: subject: has:attachment has:note has:mynote is:unread is:starred is:forwarded label: source: in:folder before: after:, and - to leave out" fez:this="searchInput" autocomplete="off">
{#if props.q}
<button type="button" class="list-search-clear" title="Clear search" onclick="fez.clearSearch()"><ui-icon name="x" size="14"></ui-icon></button>
{/if}
<a class="list-search-clear" href="#/help?topic=search" title="How to search" aria-label="How to search"><ui-icon name="question" size="16"></ui-icon></a>
</form>
{#if state.searchError}
<p class="list-search-error"><span>{state.searchError}</span><a href="#/help?topic=search">Search help</a></p>
{/if}
<div class="list-scroll" class:list-selecting={state.picked} fez:this="scroller">
{#if state.nextUp}<mail-next-up></mail-next-up>{/if}
{#if state.error}
<div class="list-state"><ui-empty icon="warning-circle" title="Could not load mail" text={state.error} compact></ui-empty></div>
{:else if state.loading}
<div class="list-loading"><ui-spinner></ui-spinner></div>
{:else if state.showEmpty}
<div class="list-state" fez:in="fade, duration=200"><ui-empty icon={state.empty.icon} title={state.empty.title} text={state.empty.text} compact></ui-empty></div>
{:else}
{#each state.rows as row}
<div class="mrow" class:unread={row.unread} class:active={row.active} class:selected={row.selected} key={row.key} data-key={row.key} title={row.title} fez:animate="flip, duration=240" fez:out="slide, duration=180" onclick="fez.openRow('{row.key}')" onpointerdown="fez.swipeStart(event, '{row.key}')">
<span class="mrow-dot"></span>
{#if row.kind === 'draft'}
<span class="mrow-draft"><ui-icon name="pencil-simple" size="18"></ui-icon></span>
{:else}
<button type="button" class="mrow-pick" aria-label={row.selected ? 'Unselect' : 'Select'} aria-pressed={row.selected ? 'true' : 'false'} onclick="fez.toggleSelect('{row.key}', event)">
<span class="mrow-face"><ui-avatar name={row.avatar} size="md"></ui-avatar></span>
<span class="mrow-check">{#if row.selected}<ui-icon name="check-square" size="22" weight="fill"></ui-icon>{:else}<ui-icon name="square" size="22"></ui-icon>{/if}</span>
</button>
{/if}
<div class="mrow-main">
<div class="mrow-line">
<span class="mrow-who">{#if row.people}{row.to ? 'To: ' : ''}{#each row.people as who, index}{index ? ', ' : ''}{who.name}{who.email ? ' ' : ''}{#if who.email}<span class="mrow-email">{who.email}</span>{/if}{/each}{#if row.more}<span class="mrow-more">{\` +\${row.more}\`}</span>{/if}{:else}{row.who}{/if}</span>
{#if row.count}<span class="mrow-count">{row.count}</span>{/if}
<span class="mrow-when">{row.when}</span>
</div>
<div class="mrow-subject">{row.subject}</div>
{#if row.preview}<div class="mrow-preview">{row.preview}</div>{/if}
{#if row.tags}
<div class="mrow-tags">
{#if row.box}<span class="mrow-pill box"><ui-icon name="tray" size="11"></ui-icon>{row.box}</span>{/if}
{#if row.via}<span class="mrow-pill via" title="Delivered to {row.via}"><ui-icon name="at" size="11"></ui-icon>{row.via}</span>{/if}
{#if row.category}<span class="mrow-pill"><ui-icon name={row.category.icon} size="11"></ui-icon>{row.category.label}</span>{/if}
{#if row.from}<span class="mrow-pill tag" style="--tag: var(--color-avatar-{row.from.color})" title={row.from.title}><ui-icon name="arrow-bend-down-right" size="11"></ui-icon>{row.from.label}</span>{/if}
{#if row.folder}<span class="mrow-pill">{row.folder}</span>{/if}
{#if row.files}<span class="mrow-pill"><ui-icon name="paperclip" size="11"></ui-icon>{row.files}</span>{/if}
{#if row.sendLabel}<ui-badge tone={row.sendTone} label={row.sendLabel}></ui-badge>{/if}
{#if row.assignee}<span class="mrow-pill owner" title={row.assigneeTitle}><ui-icon name="user" size="11"></ui-icon>{row.assignee}</span>{/if}
{#if row.done}<span class="mrow-pill done"><ui-icon name="check" size="11"></ui-icon>Done</span>{/if}
{#if row.snoozed}<span class="mrow-pill"><ui-icon name="clock" size="11"></ui-icon>{row.snoozed}</span>{/if}
{#each row.labelPills as pill}
<span class="mrow-pill tag" style="--tag: var(--color-avatar-{pill.color})">{pill.name}</span>
{/each}
</div>
{/if}
</div>
{#if row.kind === 'email'}
<button type="button" class="mrow-star" class:on={row.flagged} title={row.flagged ? 'Unstar' : 'Star'} aria-label={row.flagged ? 'Unstar' : 'Star'} onclick="fez.toggleStar('{row.id}', event)">
{#if row.flagged}<ui-icon name="star" weight="fill" size="16"></ui-icon>{:else}<ui-icon name="star" size="16"></ui-icon>{/if}
</button>
{:else if row.kind === 'draft'}
<button type="button" class="mrow-star" title="Delete draft" aria-label="Delete draft" onclick="fez.deleteDraft('{row.id}', event)"><ui-icon name="trash" size="16"></ui-icon></button>
{/if}
</div>
{/each}
{#if state.help}<ui-empty icon={state.help.icon} text={state.help.text} compact></ui-empty>{/if}
{/if}
<div class="list-more" fez:keep="sentinel" fez:this="sentinel"></div>
{#if state.loadingMore}
<div class="list-loading"><ui-spinner size="sm"></ui-spinner></div>
{/if}
</div>`
};
}

Fez('mail-list', __fez_component_0);

export default __fez_component_0;
